if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia("(min-width: 750px)");
        if (!this.elements.thumbnails) return;

        // Initialize main viewer navigation buttons
        this.initMainViewerNavigation();

        this.elements.viewer.addEventListener(
          "slideChanged",
          debounce(this.onSlideChanged.bind(this), 500)
        );
        this.elements.thumbnails
          .querySelectorAll("[data-target]")
          .forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector("button")
              .addEventListener(
                "click",
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false
                )
              );
          });
        if (
          this.dataset.desktopLayout.includes("thumbnail") &&
          this.mql.matches
        )
          this.removeListSemantic();

        // Add event listener for screen size changes
        this.mql.addEventListener("change", this.handleScreenChange.bind(this));
      }

      // Handle screen size changes
      handleScreenChange(e) {
        if (this.elements.thumbnails) {
          // Re-initialize thumbnails slider orientation on screen size change
          const thumbnailSlider = this.elements.thumbnails;
          const isDesktop = e.matches;
          const isVerticalLayout =
            this.dataset.desktopLayout === "thumbnail_slider_left" && isDesktop;

          if (thumbnailSlider.dataset.oriantation) {
            thumbnailSlider.dataset.oriantation = isVerticalLayout
              ? "vertical"
              : "horizontal";
          }
        }
      }

      // Initialize main viewer navigation buttons
      initMainViewerNavigation() {
        const mainViewerPrevButton = this.elements.viewer.querySelector(
          ".main-viewer-button--prev"
        );
        const mainViewerNextButton = this.elements.viewer.querySelector(
          ".main-viewer-button--next"
        );

        if (!mainViewerPrevButton || !mainViewerNextButton) return;

        mainViewerPrevButton.addEventListener("click", () => {
          this.navigateMainViewer("prev");
        });

        mainViewerNextButton.addEventListener("click", () => {
          this.navigateMainViewer("next");
        });
      }

      // Navigate the main viewer (prev/next)
      navigateMainViewer(direction) {
        const mediaItems = Array.from(
          this.elements.viewer.querySelectorAll(".product__media-item")
        );
        const activeMediaItem = this.elements.viewer.querySelector(
          ".product__media-item.is-active"
        );

        if (!activeMediaItem || mediaItems.length <= 1) return;

        const currentIndex = mediaItems.indexOf(activeMediaItem);
        let targetIndex;

        if (direction === "prev") {
          targetIndex =
            currentIndex - 1 < 0 ? mediaItems.length - 1 : currentIndex - 1;
        } else {
          targetIndex = (currentIndex + 1) % mediaItems.length;
        }

        const targetMediaItem = mediaItems[targetIndex];
        const mediaId = targetMediaItem.dataset.mediaId;

        // Use the setActiveMedia method to handle the navigation
        this.setActiveMedia(mediaId, false);
      }

      onSlideChanged(event) {
        const currentElement = event.detail.currentElement;
        if (!currentElement || !currentElement.dataset) {
          console.error(
            "Current element is undefined or does not have a dataset"
          );
          return; // Exit the function if currentElement is not valid
        }
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend = true) {
        const activeMedia = this.elements.viewer.querySelector(
          `[data-media-id="${mediaId}"]`
        );
        if (!activeMedia) return;

        // Get layout information
        const isMobile = !this.mql.matches;
        const desktopLayout = this.dataset.desktopLayout;

        // 1. Update active classes on all media items
        this.elements.viewer
          .querySelectorAll(".product__media-item")
          .forEach((element) => {
            element.classList.remove("is-active");
          });
        activeMedia.classList.add("is-active");

        // 2. For stacked/columns layout on desktop, move active media to first position if needed
        if (
          !isMobile &&
          prepend &&
          (desktopLayout === "stacked" || desktopLayout === "columns")
        ) {
          const mediaList = activeMedia.parentElement;
          if (mediaList && activeMedia !== mediaList.firstElementChild) {
            mediaList.prepend(activeMedia);
          }
        }

        // 3. Scroll the main slider to show the active media
        const mainSlider = this.elements.viewer.querySelector(".slider");
        if (mainSlider) {
          // Determine scroll direction - always horizontal on mobile
          const isVerticalLayout =
            desktopLayout === "thumbnail_slider_left" && !isMobile;
          const scrollProperty = isVerticalLayout ? "top" : "left";
          const offsetProperty = isVerticalLayout ? "offsetTop" : "offsetLeft";

          // Scroll to the active media
          mainSlider.scrollTo({
            [scrollProperty]: activeMedia[offsetProperty],
            behavior: "smooth",
          });
        }

        // 4. Update thumbnail active states and scroll thumbnail into view
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );
          if (activeThumbnail) {
            // Update aria-current on all thumbnails
            this.elements.thumbnails
              .querySelectorAll("button")
              .forEach((button) => button.removeAttribute("aria-current"));

            // Set aria-current on active thumbnail
            const thumbnailButton = activeThumbnail.querySelector("button");
            if (thumbnailButton) {
              thumbnailButton.setAttribute("aria-current", true);
            }

            // Get the media position for screen readers
            const mediaPosition = activeThumbnail.dataset.mediaPosition;
            if (mediaPosition) {
              this.announceLiveRegion(activeMedia, mediaPosition);
            }

            // Always scroll thumbnail into view with improved positioning
            this.scrollThumbnailIntoView(activeThumbnail);
          }
        }

        // 5. Play active media
        this.playActiveMedia(activeMedia);
        this.preventStickyHeader();

        // 6. Reset slider pagination if needed
        if (this.elements.viewer.resetPages) {
          this.elements.viewer.resetPages();
        }
      }

      // Improved method to scroll thumbnail into view
      scrollThumbnailIntoView(thumbnail) {
        if (!thumbnail) return;

        const thumbnailSlider =
          this.elements.thumbnails.querySelector(".slider");
        if (!thumbnailSlider) return;

        // Determine if the thumbnails are vertical or horizontal
        // Always horizontal on mobile regardless of desktop setting
        const isMobile = !this.mql.matches;
        const isVerticalThumbnails =
          this.elements.thumbnails.dataset.oriantation === "vertical" &&
          !isMobile;

        // Get dimensions
        const thumbnailSize = isVerticalThumbnails
          ? thumbnail.offsetHeight
          : thumbnail.offsetWidth;
        const containerSize = isVerticalThumbnails
          ? thumbnailSlider.clientHeight
          : thumbnailSlider.clientWidth;
        const scrollPos = isVerticalThumbnails
          ? thumbnailSlider.scrollTop
          : thumbnailSlider.scrollLeft;
        const thumbnailPos = isVerticalThumbnails
          ? thumbnail.offsetTop
          : thumbnail.offsetLeft;

        // Calculate the position to scroll to
        // This centers the thumbnail in the visible area when possible
        let newScrollPos;

        if (thumbnailPos < scrollPos) {
          // Thumbnail is before the visible area
          newScrollPos = Math.max(0, thumbnailPos - thumbnailSize / 2);
        } else if (thumbnailPos + thumbnailSize > scrollPos + containerSize) {
          // Thumbnail is after the visible area
          newScrollPos = thumbnailPos - containerSize / 2 + thumbnailSize / 2;
        } else {
          // Thumbnail is already visible
          return;
        }

        // Scroll to the calculated position
        thumbnailSlider.scrollTo({
          [isVerticalThumbnails ? "top" : "left"]: newScrollPos,
          behavior: "smooth",
        });
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        // 1. Update aria-current attribute on all thumbnails
        this.elements.thumbnails
          .querySelectorAll("button")
          .forEach((element) => element.removeAttribute("aria-current"));

        // 2. Set aria-current on active thumbnail
        thumbnail.querySelector("button").setAttribute("aria-current", true);

        // 3. Scroll the thumbnail into view using the enhanced method
        this.scrollThumbnailIntoView(thumbnail);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          ".product__modal-opener--image img"
        );
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute("aria-hidden", false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              "[index]",
              position
            );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute("aria-hidden", true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector("sticky-header");
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute("role", "presentation");
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute("role", "presentation")
        );
      }
    }
  );
}
